import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Sparkles, Moon, Zap, Heart, Gift, Star } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping
const iconMap = {
  Sparkles: Sparkles,
  Moon: Moon,
  Zap: Zap,
  Heart: Heart,
  Gift: Gift,
  Star: Star
};

export const Bundles = ({ onInquire }) => {
  const [bundles, setBundles] = useState([]);

  useEffect(() => {
    const fetchBundles = async () => {
      try {
        const res = await fetch(`${API_URL}/api/bundles`);
        if (res.ok) {
          const data = await res.json();
          setBundles(data.filter(b => b.enabled));
        }
      } catch (error) {
        console.error('Error fetching bundles:', error);
      }
    };
    fetchBundles();
  }, []);

  const handleInquiry = (bundle) => {
    if (onInquire) {
      onInquire({ name: `${bundle.name} Bundle`, isBundle: true });
    }
    // Scroll to contact
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (bundles.length === 0) {
    return null; // Don't show section if no bundles
  }

  return (
    <section id="bundles" className="py-20" style={{ backgroundColor: '#f5e9d0' }}>
      <div className="container mx-auto px-6">
        <div className="text-center mb-12">
          <Badge 
            className="mb-4 text-sm px-4 py-1"
            style={{ backgroundColor: '#c2a357', color: '#5e3a1c' }}
          >
            SAVE MORE
          </Badge>
          <h2
            className="text-4xl md:text-5xl font-bold mb-4"
            style={{ 
              color: '#5e3a1c',
              fontFamily: 'Georgia, serif'
            }}
          >
            Bundle & Save
          </h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: '#8b5c2f' }}>
            Curated collections for your wellness journey. Buy together and save!
          </p>
        </div>

        <div className={`grid grid-cols-1 md:grid-cols-2 ${bundles.length >= 3 ? 'lg:grid-cols-3' : ''} ${bundles.length >= 4 ? 'xl:grid-cols-4' : ''} gap-6`}>
          {bundles.map((bundle) => {
            const IconComponent = iconMap[bundle.icon] || Sparkles;
            return (
              <Card 
                key={bundle.id}
                className="relative overflow-hidden border-2 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                style={{ borderColor: bundle.color, backgroundColor: bundle.bgColor }}
              >
                {/* Savings Badge */}
                <div 
                  className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold badge-shimmer"
                  style={{ backgroundColor: bundle.color, color: 'white' }}
                >
                  {bundle.savings}
                </div>

                <CardHeader className="pb-2">
                  <div 
                    className="w-14 h-14 rounded-full flex items-center justify-center mb-4"
                    style={{ backgroundColor: bundle.color }}
                  >
                    <IconComponent className="w-7 h-7 text-white" />
                  </div>
                  <CardTitle style={{ color: '#5e3a1c' }}>{bundle.name}</CardTitle>
                  <CardDescription style={{ color: '#8b5c2f' }}>
                    {bundle.description}
                  </CardDescription>
                </CardHeader>

                <CardContent>
                  <div className="space-y-2 mb-4">
                    {bundle.products.map((product, idx) => (
                      <div 
                        key={idx}
                        className="flex items-center gap-2 text-sm"
                        style={{ color: '#5e3a1c' }}
                      >
                        <span style={{ color: bundle.color }}>✓</span>
                        {product}
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-bold" style={{ color: '#5e3a1c' }}>
                      {bundle.bundlePrice}
                    </span>
                    <span className="text-sm line-through" style={{ color: '#8b5c2f' }}>
                      {bundle.originalPrice}
                    </span>
                  </div>
                </CardContent>

                <CardFooter>
                  <Button
                    onClick={() => handleInquiry(bundle)}
                    className="w-full"
                    style={{ backgroundColor: bundle.color, color: 'white' }}
                  >
                    Get This Bundle
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {/* Free Shipping Note */}
        <div className="text-center mt-8">
          <p className="text-sm" style={{ color: '#8b5c2f' }}>
            🚚 All bundles include <strong>FREE shipping!</strong>
          </p>
        </div>
      </div>
    </section>
  );
};
